import {
  AlertColor,
  Box,
  Card,
  Grid,
  autocompleteClasses,
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import TitleText from '../../components/formlib/TitleText';
import { Formik, FormikProps } from 'formik';
import { ReactComponent as GoTo } from '../../assets/images/goTo.svg';
import MediumTypography from '../../components/formlib/MediumTypography';
import RadioComponent from '../../components/formlib/RadioComponent';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import dayjs, { Dayjs } from 'dayjs';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { useIntl } from 'react-intl';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import * as yup from 'yup';
import SmallTypography from '../../components/formlib/SmallTypography';
import {
  EligibilityDeterminationOthers,
  EligibilityDeterminationType,
} from '../eligibility/types';
import { useLocation, useNavigate } from 'react-router';
import ProgramPanel from '../../components/forms/ProgramPanel';
import SignatureComponent from '../../components/formlib/modal/SignatureComponent';
import { ReactComponent as Editsignature } from '../../assets/images/editsignature.svg';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import {
  addOrUpdateEligibilityDeterminationApi,
  getEligibilityDeterminationFormApi,
} from '../../services/configApi/eligibility/eligibilityDetermination';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import {
  BATELLE_DEVELOPMENT_INVENTORY_2,
  CLINICAL_JUDGEMENT_ROUTE,
} from '../../routes/Routing';
import ModalPopup from '../../components/formlib/ModalPopup';
import _ from 'lodash';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import { OptionType } from '../../utils/type';
import {
  DiagnosisCode,
  getCodesAndDescriptionNew,
} from '../../services/configApi/codelist/diagnosticCodes';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import { DiagnosisOptionType } from '../faceSheet/FaceSheetScreen';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import { checkOffline } from '../../services/Offline';
import { formatName } from '../../utils/nameUtils';
import moment from 'moment';
import {
  LOCAL_DATE_TIME_FORMAT,
  REFERRAL_STATUS_KEY,
} from '../../services/Constant';
import { useMultiplePermissions } from '../../hooks/usePermissions';

const initialEligibilityDeterminationValues = {
  id: '',
  appSource: null,
  dateOfEvaluation: null,
  medicalDiagnosisEligibility: null,
  notificationDate: null,
  medicalDiagnosisType: null,
  otherMedicalDiagnosis: '',
  bdiEligibility: null,
  riskFactorEligibility: null,
  pursueClinicalEligibility: null,
  eligibilitySummaryNarrative: '',
  signature: {
    signatureData: '',
    partitionKey: '',
    rowKey: '',
    id: '',
    isUpdated: false,
    name: null,
    date: null,
  },
  isOneYearDiagnosis: false,
  isThreeYearDiagnosis: false,
  isBdiTwoEligible: false,
  isRiskFactorEligible: false,
  isClinicalJudgementEligible: false,
  revokeSignature: false,
};

const validationSchema = yup.object().shape({
  medicalDiagnosisEligibility: yup
    .boolean()
    .nullable()
    .required('diagnosisEligibility.required'),
  medicalDiagnosisType: yup
    .string()
    .when('medicalDiagnosisEligibility', {
      is: true,
      then: yup.string().required('diagnosisType.required').nullable(),
      otherwise: yup.string().nullable(),
    })
    .nullable(),
  otherMedicalDiagnosis: yup.string().nullable(),
  bdiEligibility: yup.boolean().nullable().required('bdiEligibility.required'),
  riskFactorEligibility: yup
    .boolean()
    .nullable()
    .required('riskFactorEligibility.required'),
  pursueClinicalEligibility: yup
    .boolean()
    .nullable()
    .required('clinicalEligibility.required'),
  eligibilityDate: yup.string().nullable(),
  notificationDate: yup
    .string()
    .test({
      name: 'notificationDateCheck',
      test: function () {
        const { dateOfEvaluation, notificationDate } = this.parent;
        if (
          typeof notificationDate === 'string' &&
          typeof dateOfEvaluation === 'string' &&
          dayjs(notificationDate).isBefore(dateOfEvaluation)
        ) {
          return this.createError({
            path: this.path,
            message: 'notificationDateCannotPriorToDateOfEvaluationMessage',
          });
        }
        return true;
      },
    })
    .nullable(),
  eligibilitySummaryNarrative: yup.string().nullable(),
  isThreeYearDiagnosis: yup.boolean().nullable(),
  isBdiTwoEligible: yup.boolean().nullable(),
  isRiskFactorEligible: yup.boolean().nullable(),
  isClinicalJudgementEligible: yup.boolean().nullable(),
});

interface UnsavedChangesWatcherProps {
  values: EligibilityDeterminationType;
  initialValues: EligibilityDeterminationType | null;
  hasUnsavedChanges: boolean;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
}

const UnsavedChangesWatcher = ({
  values,
  initialValues,
  hasUnsavedChanges,
  onUnsavedChangesChange,
}: UnsavedChangesWatcherProps) => {
  useEffect(() => {
    if (_.isEqual(initialValues, values)) {
      onUnsavedChangesChange(false);
    } else {
      onUnsavedChangesChange(true);
    }
  }, [values, initialValues, hasUnsavedChanges]);

  return null;
};

const EligibilityDeterminationForm = () => {
  const rctl = useIntl();
  const location = useLocation();
  const [initialData, setInitialData] = useState<EligibilityDeterminationType>(
    initialEligibilityDeterminationValues,
  );
  const [evaluationId] = useState<string>(location.state?.evaluationId);
  const [uploadedToTBR, setUploadedToTBR] = useState<boolean>(
    location.state?.isUploadedToTBR,
  );
  const formikRef = useRef<FormikProps<EligibilityDeterminationType>>(null);
  const [eligibilityFormInitialValue, setEligibilityFormInitialValue] =
    useState<EligibilityDeterminationType | null>(null);
  const [eligibilityForm, setEligibilityForm] =
    useState<EligibilityDeterminationOthers | null>(null);
  const [signClicked, setSignClicked] = useState<boolean>(false);
  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [base64Signature, setBase64Signature] = useState('');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { handleUpdateUnsavedChanges, hasUnsavedChanges } = useContext(
    UnSavedChangesContext,
  );
  const [signaturePartitionKey, setSignaturePartitionKey] =
    useState<string>('');
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [selectedDiagnosisCodesList, setSelectedDiagnosisCodesList] =
    useState<OptionType | null>(null);
  const [
    initialSelectedDiagnosisCodesList,
    setInitialSelectedDiagnosisCodesList,
  ] = useState<OptionType | null>(null);
  const [diagnosisCodesList, setDiagnosisCodesList] = useState<
    DiagnosisOptionType[]
  >([]);
  const [isResetClick, setIsResetClick] = useState<boolean>(false);
  const [diagnosisRequired, setDiagnosisRequired] = useState<boolean>(false);
  const [offlineStatus, setOfflineStatus] = useState<boolean>(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  const validateForm = (value: EligibilityDeterminationType) => {
    const errors: Partial<EligibilityDeterminationType> = {};

    if (datePickerMinDateMaxDateValidate(value.dateOfEvaluation)) {
      errors.dateOfEvaluation = 'datePickerMinDateMaxDateValidate';
    }
    if (datePickerMinDateMaxDateValidate(value.notificationDate)) {
      errors.notificationDate = 'datePickerMinDateMaxDateValidate';
    }
    const hasSignature =
      value.signature !== null &&
      (value.signature.signatureData !== '' || value.signature.rowKey !== '');
    if (hasSignature && !value.notificationDate) {
      errors.notificationDate = 'notificationDateRequired';
    }
    return errors;
  };

  useEffect(() => {
    if (eligibilityForm?.bdiEligibility === null) {
      if (
        location.state?.evaluationStatus &&
        location.state.evaluationStatus === 'Eligible'
      ) {
        setEligibilityForm({ ...eligibilityForm, bdiEligibility: true });
      }
    }
  }, [eligibilityForm]);

  useEffect(() => {
    const handleOffline = async () => {
      if (await checkOffline()) {
        setOfflineStatus(true);
      } else {
        setOfflineStatus(false);
      }
    };

    handleOffline();
  }, []);

  const navigate = useNavigate();
  const offlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSignaturePartitionKey(signaturePartitionKey);
  }, [signaturePartitionKey]);

  const checkCode = (code: string | null, array: OptionType[]) => {
    const found = array.find((item) => item.id === code);
    return found ? found.label.includes('- *') : false;
  };

  const getEligibilityDeterminationData = async (
    filteredDiagnosisCodeList: DiagnosisOptionType[],
  ) => {
    toggleLoader(true);
    getEligibilityDeterminationFormApi(evaluationId)
      .then((response: EligibilityDeterminationOthers) => {
        setDiagnosisRequired(response.medicalDiagnosisEligibility ?? false);
        const result = checkCode(
          response.medicalDiagnosisType,
          filteredDiagnosisCodeList,
        );
        toggleLoader(false);
        setEligibilityForm(response);
        setSignaturePartitionKey(response.signature.partitionKey);
        const filteredDiagnosisList: DiagnosisOptionType[] = [];
        filteredDiagnosisCodeList.forEach((codeDetails) => {
          if (codeDetails.id === response.medicalDiagnosisType) {
            filteredDiagnosisList.push(codeDetails);
          }

          // Check if codeDetails.id is in the otherMedicalDiagnosis array
          const medicalCodes = response.otherMedicalDiagnosis.split(',');
          if (medicalCodes.includes(codeDetails.id)) {
            filteredDiagnosisList.push(codeDetails);
          }

          if (!codeDetails.isDeleted) {
            filteredDiagnosisList.push(codeDetails);
          }
        });
        // remove duplicates in filteredDiagnosisList
        const uniqueDiagnosisList = filteredDiagnosisList.filter(
          (item, index) => {
            return filteredDiagnosisList.indexOf(item) === index;
          },
        );

        uniqueDiagnosisList.sort((a, b) => {
          if (a.id < b.id) return -1;
          if (a.id > b.id) return 1;
          return 0;
        });

        const soertedLIst = uniqueDiagnosisList;

        setDiagnosisCodesList(soertedLIst);

        const selected: OptionType | undefined = uniqueDiagnosisList.find(
          (d) => d.id === response.medicalDiagnosisType,
        );
        if (selected) {
          setSelectedDiagnosisCodesList(selected);
          setInitialSelectedDiagnosisCodesList(selected);
        }
        formikRef.current?.setValues((values) => ({
          ...values,
          id: response.id,
          medicalDiagnosisEligibility: response.medicalDiagnosisEligibility,
          notificationDate:
            response.notificationDate === null
              ? null
              : dayjs(response.notificationDate).format('YYYY-MM-DD'),
          medicalDiagnosisType: response.medicalDiagnosisType,
          otherMedicalDiagnosis: response.otherMedicalDiagnosis.toString(),
          bdiEligibility: response.bdiEligibility,
          riskFactorEligibility: response.riskFactorEligibility,
          pursueClinicalEligibility: response.pursueClinicalEligibility,
          eligibilitySummaryNarrative: response.eligibilitySummaryNarrative,
          dateOfEvaluation:
            response.dateOfEvaluation === null
              ? dayjs(new Date()).format('YYYY-MM-DD')
              : dayjs(response.dateOfEvaluation).format('YYYY-MM-DD'),
          signature: {
            ...values.signature,
            signatureData: response.signature.signatureData,
            isUpdated: false,
            partitionKey: response.signature.partitionKey,
            rowKey: response.signature.rowKey,
          },
          isOneYearDiagnosis: response.medicalDiagnosisType
            ? result
            : response.isOneYearDiagnosis,
          isThreeYearDiagnosis: response.medicalDiagnosisType
            ? !result
            : response.isThreeYearDiagnosis,
          isBdiTwoEligible: response.isBdiTwoEligible,
          isRiskFactorEligible: response.isRiskFactorEligible,
          appSource: response.appSource,
          isClinicalJudgementEligible: response.isClinicalJudgementEligible,
          revokeSignature: response.revokeSignature,
        }));
        setInitialData((values) => ({
          ...values,
          id: response.id,
          medicalDiagnosisEligibility: response.medicalDiagnosisEligibility,
          notificationDate:
            response.notificationDate === null
              ? null
              : dayjs(response.notificationDate).format('YYYY-MM-DD'),
          medicalDiagnosisType: response.medicalDiagnosisType,
          otherMedicalDiagnosis: response.otherMedicalDiagnosis.toString(),
          bdiEligibility: response.bdiEligibility,
          riskFactorEligibility: response.riskFactorEligibility,
          pursueClinicalEligibility: response.pursueClinicalEligibility,
          eligibilitySummaryNarrative: response.eligibilitySummaryNarrative,
          dateOfEvaluation:
            response.dateOfEvaluation === null
              ? dayjs(new Date()).format('YYYY-MM-DD')
              : dayjs(response.dateOfEvaluation).format('YYYY-MM-DD'),
          signature: {
            ...values.signature,
            signatureData: response.signature.signatureData,
            isUpdated: false,
            partitionKey: response.signature.partitionKey,
            rowKey: response.signature.rowKey,
          },
          isOneYearDiagnosis: response.medicalDiagnosisType
            ? result
            : response.isOneYearDiagnosis,
          isThreeYearDiagnosis: response.medicalDiagnosisType
            ? !result
            : response.isThreeYearDiagnosis,
          isBdiTwoEligible: response.isBdiTwoEligible,
          isRiskFactorEligible: response.isRiskFactorEligible,
          appSource: response.appSource,
          isClinicalJudgementEligible: response.isClinicalJudgementEligible,
          revokeSignature: response.revokeSignature,
        }));
        setTimeout(() => {
          if (formikRef.current?.values) {
            setEligibilityFormInitialValue(formikRef.current?.values);
          }
        }, 1000);
      })
      .catch((error) => {
        toggleLoader(false);
        setOpenSnackBar(true);

        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
        }
      });
  };

  const onCancelClick = () => {
    setSignClicked(false);
  };

  const handleSuccess = (successError: AlertColor, id: string, mes: string) => {
    setOpenSnackBar(true);
    setToastrVariable(successError);
    setToastrId(id);
    setToastrDefaultMessage(mes);
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  const checkPermissionToHide = () => {
    if (uploadedToTBR) {
      return (
        !checkPermissionForFeature(
          'backend.update_intake_after_lock',
          'editPermission',
        ) || !checkPermissionForFeature('backend.clients', 'editPermission')
      );
    }
    return !checkPermissionForFeature('backend.clients', 'editPermission');
  };

  const signatureSaveOrUPdate = (signed: string) => {
    const formatedUserName = formatName(
      localStorage.getItem('userFirstName'),
      null,
      localStorage.getItem('userLastName'),
      null,
    );
    setBase64Signature(signed);
    setSignClicked(!signClicked);
    setIsResetClick(false);
    formikRef.current?.setValues((values) => ({
      ...values,
      signature: {
        ...values.signature,
        signatureData: signed,
        isUpdated: true,
        name: formatedUserName,
        date: dayjs.utc().format(),
      },
    }));
  };

  const submitEligibilityDeterminationResponse = async (
    vals: EligibilityDeterminationType,
  ) => {
    toggleLoader(true);
    if (isResetClick === true) {
      vals.signature.signatureData = '';
      vals.signature.partitionKey = '';
      vals.signature.rowKey = '';
      vals.signature.isUpdated = true;
    }
    if (eligibilityForm) {
      const obj: EligibilityDeterminationOthers = {
        client: eligibilityForm.client,
        parent: eligibilityForm.parent,
        program: eligibilityForm.program,
        ...vals,
      };
      addOrUpdateEligibilityDeterminationApi(evaluationId, obj)
        .then(() => {
          toggleLoader(false);
          setOpenSnackBar(true);
          setToastrVariable('success');

          if (vals.id) {
            setToastrId('eligibilityDetermination.updateText');
            setToastrDefaultMessage(
              'Eligibility Determination Updated Successfully',
            );
          } else {
            setToastrId('eligibilityDetermination.successText');
            setToastrDefaultMessage(
              'Eligibility Determination Saved Successfully',
            );
          }
          getEligibilityDeterminationData(diagnosisCodesList);
        })
        .catch((error) => {
          toggleLoader(false);
          setOpenSnackBar(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('eligibilityDetermination.errorText');
            setToastrDefaultMessage(
              'Failed to update Eligibility Determination',
            );
          }
        });
    }
  };

  const resetCallBackForTypeEmployee = () => {
    setSignClicked(!signClicked);
    setIsResetClick(true);

    formikRef.current?.setValues((values) => ({
      ...values,
      signature: {
        ...values.signature,
        signatureData: '',
        isUpdated: true,
        partitionKey: '',
        rowKey: '',
      },
      revokeSignature: true,
    }));
    setUploadedToTBR(false);
    setSignaturePartitionKey('');
  };

  const hasEvalAdd = checkPermissionForFeature(
    'backend.evaluations',
    'addPermission',
  );
  const hasEvalEdit = checkPermissionForFeature(
    'backend.evaluations',
    'editPermission',
  );
  const hasId = !!eligibilityForm?.id;

  const isScreenLocked = useMemo(() => {
    return (
      !hasEditPermission || (!hasId && !hasEvalAdd) || (hasId && !hasEvalEdit)
    );
  }, [hasEditPermission, hasId, hasEvalAdd, hasEvalEdit]);

  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offlineStatus || isScreenLocked) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [offlineStatus, isScreenLocked]);

  useEffect(() => {
    const handleDiagnosisDataGetAPI = async () => {
      toggleLoader(true);
      getCodesAndDescriptionNew()
        .then((response: DiagnosisCode[]) => {
          const diagnosisCodeList: DiagnosisOptionType[] = [];
          response.forEach((codeDetails) => {
            if (!codeDetails.isDeleted && codeDetails.qualifyingDiagnosis) {
              diagnosisCodeList.push({
                id: codeDetails.code,
                label: codeDetails.oneYearEligibility
                  ? `${codeDetails.code} - * ${codeDetails.description}`
                  : `${codeDetails.code} - ${codeDetails.description}`,
                isDeleted: codeDetails.isDeleted ?? false,
              });
            }
          });
          getEligibilityDeterminationData(diagnosisCodeList);
        })
        .catch((error) => {
          toggleLoader(false);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId(error);
            setToastrDefaultMessage(error);
          }
        });
    };

    handleDiagnosisDataGetAPI();
  }, []);
  return (
    <Box component="main">
      <ModalPopup
        description="formUnsavedChangesMessage"
        open={showCancelDialog}
        onCancel={() => {
          setShowCancelDialog(false);
        }}
        onOk={() => {
          if (formikRef.current) {
            formikRef.current?.setValues(initialData);
            setUploadedToTBR(location.state?.isUploadedToTBR);
            setSignaturePartitionKey(
              eligibilityFormInitialValue === null
                ? ''
                : eligibilityFormInitialValue.signature.partitionKey,
            );
            setSelectedDiagnosisCodesList(initialSelectedDiagnosisCodesList);
            setDiagnosisRequired(
              initialData.medicalDiagnosisEligibility ?? false,
            );
            setShowCancelDialog(false);
          }
        }}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
      />
      <ModalPopup
        open={openUnSavedModal}
        description="UnsavedAlertDescriptionWhileLeaving"
        labelId1="UnSavedAlertCancel"
        labelId2="UnSavedAlertLeave"
        negativeActionLabel="No"
        positiveActionLabel="Yes"
        onCancel={() => {
          setOpenUnSavedModal(false);
        }}
        onOk={() => {
          navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
          handleUpdateUnsavedChanges(false);
          setOpenUnSavedModal(false);
        }}
      />
      <SnackBarComponent
        open={openSnackBar}
        handleClose={handleCloseSnakBar}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <Box component="section">
        <Box component="div" className="rowContainer">
          <CustomBackArrow
            onClick={() => {
              if (hasUnsavedChanges) {
                setOpenUnSavedModal(true);
              } else {
                handleUpdateUnsavedChanges(false);
                navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
              }
            }}
          />
          <Grid container>
            <Grid item xs={6} lg={6}>
              <Box className="flex__  align__items__center">
                <TitleText
                  labelid="eligibilityDetermination_text"
                  defaultlabel="Eligibility Determination"
                  Sxprops={{ ml: 1 }}
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={6}
              lg={6}
              className="flex__ align__items__center justifyContent-FlexEnd"
            ></Grid>
          </Grid>
        </Box>
        <Box
          ref={parentRef}
          style={{
            pointerEvents: isScreenLocked ? 'none' : 'auto',
            opacity: isScreenLocked ? '0.8' : '1 ',
          }}
        >
          <Box component="div">
            <Formik
              initialValues={initialEligibilityDeterminationValues}
              validateOnMount={true}
              innerRef={formikRef}
              validateOnChange={true}
              validationSchema={validationSchema}
              validate={validateForm}
              onSubmit={(values) => {
                values.signature.signatureData = base64Signature;
                submitEligibilityDeterminationResponse(values);
              }}
            >
              {({ values, errors, touched, setFieldValue, handleSubmit }) => {
                return (
                  <>
                    <UnsavedChangesWatcher
                      values={values}
                      initialValues={eligibilityFormInitialValue}
                      hasUnsavedChanges={hasUnsavedChanges}
                      onUnsavedChangesChange={handleUpdateUnsavedChanges}
                    />
                    {console.log('asdasd', errors)}

                    <Card className="formCardview">
                      {eligibilityForm && (
                        <ProgramPanel
                          program={eligibilityForm.program}
                          client={eligibilityForm.client}
                          parents={
                            eligibilityForm.parent
                              ? [eligibilityForm.parent]
                              : []
                          }
                          showIntakeFacilitator={true}
                        />
                      )}

                      <Box component="div">
                        <Box
                          className="horizontalLine"
                          sx={{ marginY: '8px' }}
                        />
                      </Box>
                      <Box component="div">
                        <Grid container rowSpacing={'24px'} className="pt-md">
                          <Grid item xs={4}>
                            {values.dateOfEvaluation !== null && (
                              <DatePickerComponent
                                disabledDate={true}
                                labelid="eligibilityDetermination.eligibilityDate"
                                disableFuture={false}
                                defaultlabelid="Date of Evaluation"
                                value={
                                  values.dateOfEvaluation
                                    ? dayjs(values.dateOfEvaluation)
                                    : dayjs(new Date())
                                }
                                handlechange={(datevalue: Dayjs | null) => {
                                  const formattedDate =
                                    dayjs(datevalue).format('YYYY-MM-DD');
                                  if (datevalue === null) {
                                    setFieldValue('eligibilityDate', null);
                                  } else {
                                    setFieldValue(
                                      'eligibilityDate',
                                      formattedDate,
                                    );
                                  }
                                }}
                              />
                            )}
                            {values.dateOfEvaluation === null && (
                              <DatePickerComponent
                                disabledDate={
                                  uploadedToTBR || signaturePartitionKey !== ''
                                }
                                labelid="eligibilityDetermination.eligibilityDate"
                                disableFuture={false}
                                defaultlabelid="Date of Evaluation"
                                disableManualInput={
                                  signaturePartitionKey !== ''
                                }
                                value={null}
                                handlechange={(datevalue: Dayjs | null) => {
                                  const formattedDate =
                                    dayjs(datevalue).format('YYYY-MM-DD');
                                  if (datevalue === null) {
                                    setFieldValue('eligibilityDate', null);
                                  } else {
                                    setFieldValue(
                                      'eligibilityDate',
                                      formattedDate,
                                    );
                                  }
                                  setFieldValue('notificationDate', null);
                                }}
                              />
                            )}
                            {errors.dateOfEvaluation &&
                              touched.dateOfEvaluation && (
                                <MediumTypography
                                  labelid={errors.dateOfEvaluation}
                                  className="errorText-md"
                                />
                              )}
                          </Grid>
                          <Grid item xs={0.3}></Grid>
                          <Grid item xs={4}>
                            <DatePickerComponent
                              disabledDate={
                                uploadedToTBR || signaturePartitionKey !== ''
                              }
                              labelid="eligibilityDetermination.notificationDateText"
                              disableFuture={false}
                              disableManualInput={signaturePartitionKey !== ''}
                              defaultlabelid="Date of Notification"
                              value={
                                values.notificationDate
                                  ? dayjs(values.notificationDate)
                                  : null
                              }
                              minDate={
                                values.dateOfEvaluation
                                  ? dayjs(values.dateOfEvaluation)
                                  : undefined
                              }
                              required={
                                values.signature !== null &&
                                (values.signature.signatureData !== '' ||
                                  values.signature.rowKey !== '')
                              }
                              handlechange={(datevalue: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(datevalue).format('YYYY-MM-DD');
                                if (datevalue === null) {
                                  setFieldValue('notificationDate', null);
                                } else {
                                  setFieldValue(
                                    'notificationDate',
                                    formattedDate,
                                  );
                                }
                              }}
                            />

                            {errors.notificationDate &&
                              touched.notificationDate && (
                                <MediumTypography
                                  labelid={errors.notificationDate}
                                  className="errorText-md"
                                />
                              )}
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          rowSpacing={'16px'}
                          columnSpacing={'40px'}
                        >
                          <Grid
                            item
                            xs={4.5}
                            lg={4.5}
                            sx={{ marginY: '5px' }}
                          ></Grid>

                          <Grid item xs={12} lg={12}>
                            <MediumTypography
                              labelid="eligibilityDetermination.diagnosisEligibility"
                              defaultlabel="Medical Diagnosis Eligibility (Evaluation Diagnosis)"
                              fontweight={600}
                              fontSize="16px"
                              textColor="#2A4241"
                            />
                          </Grid>
                          <Grid item xs={4.5}>
                            <RadioComponent
                              disable={
                                uploadedToTBR || signaturePartitionKey !== ''
                              }
                              value={values.medicalDiagnosisEligibility}
                              name="multipleBirth"
                              handleChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                if (JSON.parse(e.target.value) === true) {
                                  setDiagnosisRequired(true);
                                } else {
                                  setDiagnosisRequired(false);
                                  setSelectedDiagnosisCodesList(null);
                                  setFieldValue('medicalDiagnosisType', '');
                                  setFieldValue('isOneYearDiagnosis', false);
                                  setFieldValue('isThreeYearDiagnosis', false);
                                  setFieldValue('otherMedicalDiagnosis', '');
                                }
                                setFieldValue(
                                  'medicalDiagnosisEligibility',
                                  JSON.parse(e.target.value),
                                );
                              }}
                              labelid=""
                              defaultlabelid=""
                              labelidA="eligibilityDetermination.eligibleText"
                              defaultlabelidA="Eligible"
                              labelidB="eligibilityDetermination.nonEligibleText"
                              defaultlabelidB="Not Eligible"
                            />
                            {errors.medicalDiagnosisEligibility &&
                              touched.medicalDiagnosisEligibility && (
                                <SmallTypography
                                  sxProps={{ color: 'red' }}
                                  labelId={errors.medicalDiagnosisEligibility}
                                  defaultLabelId="Medical Diagnosis Eligibility is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>

                          <Grid item xs={9} lg={9}>
                            <AutocompleteSearch
                              disabled={
                                uploadedToTBR ||
                                !diagnosisRequired ||
                                signaturePartitionKey !== ''
                              }
                              renderOption={(props, option: OptionType) => {
                                const parts = option.label.split(' - ');
                                let beforeHyphen = '';
                                let afterHyphen = '';

                                if (parts.length > 1) {
                                  beforeHyphen = parts[0];
                                  afterHyphen = parts?.slice(1)?.join(' - ');
                                } else {
                                  beforeHyphen = option.label;
                                }

                                const underlineStyle = afterHyphen.includes('*')
                                  ? 'underline'
                                  : 'none';

                                return (
                                  <li {...props}>
                                    {parts.length > 1 ? (
                                      <>
                                        <span>{beforeHyphen} - </span>
                                        <span
                                          style={{
                                            textDecoration: underlineStyle,
                                          }}
                                        >
                                          {afterHyphen}
                                        </span>
                                      </>
                                    ) : (
                                      <span
                                        style={{
                                          textDecoration: underlineStyle,
                                        }}
                                      >
                                        {beforeHyphen}
                                      </span>
                                    )}
                                  </li>
                                );
                              }}
                              textDecoration={
                                selectedDiagnosisCodesList?.label.includes(
                                  '- *',
                                )
                                  ? 'underline'
                                  : 'none'
                              }
                              Required={diagnosisRequired}
                              labelId={
                                'eligibilityDetermination.selectDiagnosisText'
                              }
                              defaultId="Select Evaluation Diagnosis *"
                              optionHintId={'noDiagnosisDropdownTextId'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                },
                                backgroundColor: 'white',
                              }}
                              data={diagnosisCodesList}
                              selectedValue={selectedDiagnosisCodesList ?? null}
                              onSelectItem={(value: OptionType | null) => {
                                if (value === null) {
                                  setSelectedDiagnosisCodesList(null);
                                  setFieldValue('medicalDiagnosisType', '');
                                  setFieldValue('isOneYearDiagnosis', false);
                                  setFieldValue('isThreeYearDiagnosis', false);
                                } else {
                                  const hasStarPrefix =
                                    value.label.includes('- *');
                                  values.medicalDiagnosisType = value.id;
                                  if (hasStarPrefix) {
                                    values.isOneYearDiagnosis = true;
                                    values.isThreeYearDiagnosis = false;
                                  } else {
                                    setFieldValue('isThreeYearDiagnosis', true);
                                    setFieldValue('isOneYearDiagnosis', false);
                                    values.isOneYearDiagnosis = false;
                                    values.isThreeYearDiagnosis = true;
                                  }
                                  formikRef.current?.setValues(values);
                                  setSelectedDiagnosisCodesList(value);
                                }
                              }}
                              disableLabel={false}
                            />
                            {errors.medicalDiagnosisType &&
                              touched.medicalDiagnosisType && (
                                <MediumTypography
                                  labelid={errors.medicalDiagnosisType}
                                  className="errorText-md"
                                />
                              )}
                          </Grid>

                          <Grid item xs={9} lg={9} className="mt-sm">
                            <MultipleAutocompleteSearch
                              disabled={
                                uploadedToTBR ||
                                !diagnosisRequired ||
                                signaturePartitionKey !== ''
                              }
                              labelId="IntakeCurrentHealth.othermedicalDiagnosis"
                              defaultId="Other Known Medical Diagnosis"
                              data={diagnosisCodesList}
                              type="string"
                              onSelectItem={(value: OptionType[] | null) => {
                                if (value) {
                                  const selectedMedicalDiagnosisIds = value.map(
                                    (r) => r.id,
                                  );
                                  setFieldValue(
                                    'otherMedicalDiagnosis',
                                    selectedMedicalDiagnosisIds.toString(),
                                  );
                                }
                              }}
                              disableLabel={true}
                              optionHintId={'No Medical Diagnosis found'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                  marginTop: '2px',
                                },
                                backgroundColor: '#fff',
                              }}
                              value={values.otherMedicalDiagnosis
                                .split(',') // Split the string by comma
                                .map((r) => ({
                                  id: r,
                                  label:
                                    diagnosisCodesList.find(
                                      (dCl) => dCl.id === r,
                                    )?.label ?? '', // Set label to empty string if not found
                                }))
                                .filter(
                                  (
                                    option,
                                  ): option is { id: string; label: string } =>
                                    option.label !== '', // Filter out options with empty labels
                                )}
                            />
                            {errors.otherMedicalDiagnosis &&
                              touched.otherMedicalDiagnosis && (
                                <MediumTypography
                                  className="errorText-md"
                                  labelid={errors.otherMedicalDiagnosis}
                                />
                              )}
                          </Grid>

                          <Grid item xs={4.5}></Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={12} lg={12}>
                            <MediumTypography
                              labelid="eligibilityDetermination.BdiText"
                              defaultlabel="BDI Eligibility"
                              fontweight={600}
                              fontSize="16px"
                              textColor="#2A4241"
                            />
                          </Grid>
                          <Grid item xs={4.5}>
                            <RadioComponent
                              disable={
                                uploadedToTBR || signaturePartitionKey !== ''
                              }
                              value={values.bdiEligibility}
                              name="multipleBirth"
                              handleChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                if (JSON.parse(e.target.value) === true) {
                                  setFieldValue('isBdiTwoEligible', true);
                                } else {
                                  setFieldValue('isBdiTwoEligible', false);
                                }

                                setFieldValue(
                                  'bdiEligibility',
                                  JSON.parse(e.target.value),
                                );
                              }}
                              labelid=""
                              defaultlabelid=""
                              labelidA="eligibilityDetermination.eligibleText"
                              defaultlabelidA="Eligible"
                              labelidB="eligibilityDetermination.nonEligibleText"
                              defaultlabelidB="Not Eligible"
                            />
                            {errors.bdiEligibility &&
                              touched.bdiEligibility && (
                                <SmallTypography
                                  sxProps={{ color: 'red' }}
                                  labelId={errors.bdiEligibility}
                                  defaultLabelId="BDI Eligibility is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={4.5}></Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={12} lg={12}>
                            <MediumTypography
                              labelid="eligibilityDetermination.riskFactorText"
                              defaultlabel="Risk Factor Eligibility"
                              fontweight={600}
                              fontSize="16px"
                              textColor="#2A4241"
                            />
                          </Grid>
                          <Grid item xs={4.5}>
                            <RadioComponent
                              disable={
                                uploadedToTBR || signaturePartitionKey !== ''
                              }
                              value={values.riskFactorEligibility}
                              name="multipleBirth"
                              handleChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                if (JSON.parse(e.target.value) === true) {
                                  setFieldValue('isRiskFactorEligible', true);
                                } else {
                                  setFieldValue('isRiskFactorEligible', false);
                                }

                                setFieldValue(
                                  'riskFactorEligibility',
                                  JSON.parse(e.target.value),
                                );
                              }}
                              labelid=""
                              defaultlabelid=""
                              labelidA="eligibilityDetermination.eligibleText"
                              defaultlabelidA="Eligible"
                              labelidB="eligibilityDetermination.nonEligibleText"
                              defaultlabelidB="Not Eligible"
                            />
                            {errors.riskFactorEligibility &&
                              touched.riskFactorEligibility && (
                                <SmallTypography
                                  sxProps={{ color: 'red' }}
                                  labelId={errors.riskFactorEligibility}
                                  defaultLabelId="Risk Factor Eligibility is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={4.5}></Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={12} lg={12} display={'flex'}>
                            <MediumTypography
                              labelid="eligibilityDetermination.pursueClinicalText"
                              defaultlabel="Clinical Judgment"
                              fontweight={600}
                              fontSize="16px"
                              textColor="#2A4241"
                            />
                            <Box
                              className="flex__ align__items__center ml-sm"
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (
                                  _.isEqual(eligibilityFormInitialValue, values)
                                ) {
                                  navigate(CLINICAL_JUDGEMENT_ROUTE, {
                                    state: {
                                      evaluationId: evaluationId,
                                      isUploadedToTBR: uploadedToTBR,
                                    },
                                  });
                                } else {
                                  setShowCancelDialog(true);
                                }
                              }}
                            >
                              <GoTo />
                            </Box>
                          </Grid>
                          <Grid item xs={4.5}>
                            <RadioComponent
                              disable={
                                uploadedToTBR || signaturePartitionKey !== ''
                              }
                              value={values.isClinicalJudgementEligible}
                              name="multipleBirth"
                              handleChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                formikRef.current?.setValues((value) => ({
                                  ...value,
                                  isClinicalJudgementEligible: JSON.parse(
                                    e.target.value,
                                  ),
                                  pursueClinicalEligibility: JSON.parse(
                                    e.target.value,
                                  ),
                                }));
                              }}
                              labelid=""
                              defaultlabelid=""
                              labelidA="eligibilityDetermination.eligibleText"
                              defaultlabelidA="Eligible"
                              labelidB="eligibilityDetermination.nonEligibleText"
                              defaultlabelidB="Not Eligible"
                            />
                            {errors.pursueClinicalEligibility &&
                              touched.pursueClinicalEligibility && (
                                <SmallTypography
                                  sxProps={{ color: 'red' }}
                                  labelId={errors.pursueClinicalEligibility}
                                  defaultLabelId="Clinical Judgment is required"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid item xs={4.5}></Grid>
                          <Grid item xs={4.5} lg={4.5}></Grid>
                          <Grid
                            item
                            xs={12}
                            lg={12}
                            sx={{ marginBottom: '1%' }}
                          >
                            <MediumTypography
                              labelid="eligibilityDetermination.officialBasisText"
                              defaultlabel="Official Basis for Eligibility"
                              fontweight={600}
                              fontSize="16px"
                              textColor="#2A4241"
                            />
                          </Grid>
                        </Grid>

                        <Box
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0px',
                          }}
                        >
                          <Box style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ flex: '1 0 33%' }}>
                              <MediumTypography
                                labelid="eligibilityDetermination.eligibilityBasisText"
                                defaultlabel="Eligibility Basis"
                                fontweight={500}
                                fontSize="14px"
                                textColor="#97A6A5"
                              />
                            </div>
                            <div style={{ flex: '1 0 33%' }}>
                              <MediumTypography
                                labelid="eligibilityDetermination.reEvalutationDueText"
                                defaultlabel="Re-evaluation Due"
                                fontweight={500}
                                fontSize="14px"
                                textColor="#97A6A5"
                              />
                            </div>
                            <div style={{ flex: '1 0 33%' }}></div>
                          </Box>

                          <Box style={{ display: 'flex', gap: '20px' }}>
                            <Box style={{ flex: '1 0 33%' }}>
                              <CheckBoxComponent
                                disabled={true}
                                labelid="eligibilityDetermination.3YearDiagnosisText"
                                defaultlabelid="3 Year Diagnosis"
                                ischecked={values.isThreeYearDiagnosis}
                                CheckHandleChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (values.isThreeYearDiagnosis === true) {
                                    if (e.target.checked === false) {
                                      setFieldValue(
                                        'isThreeYearDiagnosis',
                                        e.target.checked,
                                      );
                                    } else {
                                      e.preventDefault();
                                    }
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                flex: '1 0 33%',
                                position: 'relative',
                                top: '10px',
                              }}
                            >
                              <MediumTypography
                                labelid="eligibilityDetermination.untilThreeYearsText"
                                defaultlabel="Until 3 years"
                                fontweight={400}
                                fontSize="14px"
                                textColor="#2A4241"
                              />
                            </Box>
                            <Box style={{ flex: '1 0 33%' }}></Box>
                          </Box>

                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />
                          <Box style={{ display: 'flex', gap: '20px' }}>
                            <Box style={{ flex: '1 0 33%' }}>
                              <CheckBoxComponent
                                disabled={true}
                                labelid="eligibilityDetermination.1YearDiagnosisText"
                                defaultlabelid="1 Year Diagnosis"
                                ischecked={values.isOneYearDiagnosis}
                                CheckHandleChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (values.isOneYearDiagnosis === true) {
                                    if (e.target.checked === false) {
                                      setFieldValue(
                                        'isOneYearDiagnosis',
                                        e.target.checked,
                                      );
                                    } else {
                                      e.preventDefault();
                                    }
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                flex: '1 0 33%',
                                position: 'relative',
                                top: '10px',
                              }}
                            >
                              <MediumTypography
                                labelid="eligibilityDetermination.everyOneYearText"
                                defaultlabel="At 1 year"
                                fontweight={400}
                                fontSize="14px"
                                textColor="#2A4241"
                              />
                            </Box>
                            <Box style={{ flex: '1 0 33%' }}></Box>
                          </Box>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />
                          <Box
                            style={{
                              display: 'flex',
                              gap: '20px',
                            }}
                          >
                            <Box style={{ flex: '1 0 33%' }}>
                              <CheckBoxComponent
                                disabled={true}
                                labelid="eligibilityDetermination.BdiText"
                                defaultlabelid="BDI Eligibility"
                                ischecked={values.bdiEligibility ?? false}
                                CheckHandleChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (values.isBdiTwoEligible === true) {
                                    if (e.target.checked === false) {
                                      setFieldValue(
                                        'isBdiTwoEligible',
                                        e.target.checked,
                                      );
                                    } else {
                                      e.preventDefault();
                                    }
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                flex: '1 0 33%',
                                position: 'relative',
                                top: '10px',
                              }}
                            >
                              <MediumTypography
                                labelid="eligibilityDetermination.everyOneYearText"
                                defaultlabel="At 1 year"
                                fontweight={400}
                                fontSize="14px"
                                textColor="#2A4241"
                              />
                            </Box>
                            <Box style={{ flex: '1 0 33%' }}></Box>
                          </Box>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '0px' }}
                          />
                          <Box
                            style={{
                              display: 'flex',
                              gap: '20px',
                            }}
                          >
                            <Box style={{ flex: '1 0 33%' }}>
                              <CheckBoxComponent
                                disabled={true}
                                labelid="eligibilityDetermination.riskFacText"
                                defaultlabelid="Risk Factor"
                                ischecked={values.isRiskFactorEligible}
                                CheckHandleChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (values.isRiskFactorEligible === true) {
                                    if (e.target.checked === false) {
                                      setFieldValue(
                                        'isRiskFactorEligible',
                                        e.target.checked,
                                      );
                                    } else {
                                      e.preventDefault();
                                    }
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                flex: '1 0 33%',
                                position: 'relative',
                                top: '10px',
                              }}
                            >
                              <MediumTypography
                                labelid="eligibilityDetermination.everyOneYearText"
                                defaultlabel="At 1 year"
                                fontweight={400}
                                fontSize="14px"
                                textColor="#2A4241"
                              />
                            </Box>
                            <Box style={{ flex: '1 0 33%' }}></Box>
                          </Box>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '1px' }}
                          />
                          <Box
                            style={{
                              display: 'flex',
                              gap: '20px',
                            }}
                          >
                            <Box style={{ flex: '1 0 33%' }}>
                              <CheckBoxComponent
                                disabled={true}
                                labelid="eligibilityDetermination.clinicalJudgment"
                                defaultlabelid="Clinical Judgement"
                                ischecked={values.isClinicalJudgementEligible}
                                CheckHandleChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  if (
                                    values.isClinicalJudgementEligible === true
                                  ) {
                                    if (e.target.checked === false) {
                                      setFieldValue(
                                        'isClinicalJudgementEligible',
                                        e.target.checked,
                                      );
                                    } else {
                                      e.preventDefault();
                                    }
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Box>
                            <Box
                              style={{
                                flex: '1 0 33%',
                                position: 'relative',
                                top: '10px',
                              }}
                            >
                              <MediumTypography
                                labelid="eligibilityDetermination.everySixMonthsText"
                                defaultlabel="At 6 months"
                                fontweight={400}
                                fontSize="14px"
                                textColor="#2A4241"
                              />
                            </Box>
                            <Box style={{ flex: '1 0 33%' }}></Box>
                          </Box>
                          <Box
                            className="horizontalLine"
                            sx={{ marginY: '1px' }}
                          />
                        </Box>

                        <Grid item xs={10} sx={{ marginTop: '2%' }}>
                          <CommentTextbox
                            disabled={
                              uploadedToTBR || signaturePartitionKey !== ''
                            }
                            maxLength={2000}
                            rows={4}
                            name="immunizationComment"
                            placeholder={rctl.formatMessage({
                              id: 'eligibilityDetermination.commentBoxPlaceText',
                            })}
                            handlechange={(value: string) => {
                              setFieldValue(
                                'eligibilitySummaryNarrative',
                                value,
                              );
                            }}
                            Value={values.eligibilitySummaryNarrative}
                          />
                        </Grid>
                        <Box
                          ref={offlineRef}
                          style={{
                            pointerEvents:
                              !offlineStatus && isScreenLocked
                                ? 'none'
                                : 'auto',
                            opacity:
                              !offlineStatus && isScreenLocked
                                ? '0.8'
                                : 'unset',
                          }}
                        >
                          {values.appSource !== 'TBR' && (
                            <Grid item xs={10}>
                              <MediumTypography
                                labelid="signatureText"
                                defaultlabel="Signature"
                                className="mainText-xlg pt-md pb-md"
                              />

                              {values.signature?.name && (
                                <Box className="flex__">
                                  <MediumTypography
                                    labelid="EICS.Name"
                                    defaultlabel="Name:"
                                    className="pb-md"
                                  />
                                  <MediumTypography
                                    label={values.signature.name ?? ''}
                                    className="ml-sm pb-md"
                                  />
                                </Box>
                              )}

                              {values.signature?.date && (
                                <Box className="flex__">
                                  <MediumTypography
                                    labelid="dateLabel"
                                    defaultlabel="Date:"
                                    className="pb-md"
                                  />
                                  <MediumTypography
                                    label={
                                      values.signature.date
                                        ? moment(
                                            values.signature.date as string,
                                          )
                                            .utc()
                                            .local()
                                            .format(LOCAL_DATE_TIME_FORMAT)
                                        : '-'
                                    }
                                    className="ml-sm pb-md"
                                  />
                                </Box>
                              )}

                              {values.signature !== null &&
                                (values.signature.signatureData !== '' ||
                                  values.signature.rowKey !== '') && (
                                  <ButtonComponent
                                    className="btn-primary btn-cancel signature_view_Box"
                                    labelId={'ProgressNoteViewSignature'}
                                    defaultLabelId={'View signature'}
                                    variantType="outlined"
                                    LeftIcon={<Editsignature />}
                                    onClick={() => setSignClicked(true)}
                                  />
                                )}

                              {values.signature !== null &&
                                values.signature.signatureData === '' &&
                                values.signature.rowKey === '' && (
                                  <ButtonComponent
                                    disabled={
                                      uploadedToTBR ||
                                      !checkPermissionForFeature(
                                        'backend.clients',
                                        'editPermission',
                                      )
                                    }
                                    className="btn-primary btn-cancel signature_add_Box"
                                    labelId="Insurance.Addsignature"
                                    defaultLabelId="Add Signature"
                                    variantType="outlined"
                                    onClick={() => {
                                      setSignClicked(true);
                                    }}
                                  />
                                )}
                            </Grid>
                          )}
                        </Box>

                        {values.appSource === 'TBR' && (
                          <Grid item xs={12}>
                            <MediumTypography
                              className="font-bold mt-md"
                              labelid="evaluationcreatedMessageFromTBR"
                              defaultlabel="* Evaluation created in TBR"
                            />
                          </Grid>
                        )}
                      </Box>
                    </Card>
                    <Box className="mb-xlg">
                      <Grid
                        className="flex__ justifyContent-FlexEnd pt-md"
                        container
                        direction="row"
                        alignItems="right"
                      >
                        <Grid item>
                          <ButtonComponent
                            sxProps={{
                              display: isScreenLocked ? 'none' : 'auto',
                            }}
                            disabled={uploadedToTBR}
                            className="btn-primary btn-cancel mr-md"
                            variantType="contained"
                            defaultLabelId="Cancel"
                            labelId="Contacts.cancelbtn"
                            onClick={() => {
                              if (
                                _.isEqual(eligibilityFormInitialValue, values)
                              ) {
                                navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
                              } else {
                                setShowCancelDialog(true);
                              }
                            }}
                          />

                          {eligibilityForm?.id === '' ||
                            (eligibilityForm?.id === null && (
                              <ButtonComponent
                                sxProps={{
                                  display: isScreenLocked ? 'none' : 'auto',
                                }}
                                disabled={uploadedToTBR}
                                className="btn-primary btn-submit"
                                variantType="contained"
                                type="submit"
                                labelId="Insurance.save"
                                defaultLabelId="Save"
                                onClick={handleSubmit}
                              />
                            ))}
                          {eligibilityForm?.id && (
                            <ButtonComponent
                              disabled={
                                !!values.signature.rowKey ||
                                (!values.revokeSignature && uploadedToTBR)
                              }
                              sxProps={{
                                display: isScreenLocked ? 'none' : 'auto',
                              }}
                              className="btn-primary btn-submit"
                              variantType="contained"
                              type="submit"
                              labelId="clientpage.Update"
                              defaultLabelId="Update"
                              onClick={handleSubmit}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                    {signClicked && (
                      <Box component="div">
                        <SignatureComponent
                          signClicked={signClicked}
                          hideRemoveButton={checkPermissionToHide()}
                          signature={values.signature.signatureData}
                          labelId="EmployeeSignatureText"
                          defaultLabelId="Employee Signature"
                          onCancelClick={onCancelClick}
                          signaturePartitionKey={values.signature.partitionKey}
                          signatureRowKey={values.signature.rowKey}
                          signatureSaveOrUPdate={signatureSaveOrUPdate}
                          handleSuccess={handleSuccess}
                          resetCallBackForTypeEmployee={
                            resetCallBackForTypeEmployee
                          }
                          type="Employee"
                        />
                      </Box>
                    )}
                  </>
                );
              }}
            </Formik>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EligibilityDeterminationForm;
